import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { FileText, Loader as Loader2, X } from 'lucide-react';
import { useB2BUser } from './B2BUserContext';
import { TERMS_SECTIONS, acceptTerms, fetchEffectiveTerms, type EffectiveTerms } from '@/lib/b2bTerms';

interface B2BTermsContextType {
  terms: EffectiveTerms | null;
  showTerms: boolean;
  needsAcceptance: boolean;
  accept: () => Promise<boolean>;
  remindIfNeeded: () => Promise<void>;
}

const B2BTermsContext = createContext<B2BTermsContextType | undefined>(undefined);

export function B2BTermsProvider({ children }: { children: ReactNode }) {
  const { session } = useB2BUser();
  const clientId = session?.activeClientId ?? null;
  const isAgent = session?.role === 'agent';
  const clientName = session?.activeClientRecord?.name ?? '';

  const [terms, setTerms] = useState<EffectiveTerms | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const resolverRef = useRef<(() => void) | null>(null);
  const shownForRef = useRef<string | null>(null);

  const needsAcceptance = Boolean(terms?.published && !terms.accepted);

  useEffect(() => {
    let cancelled = false;
    setTerms(null);
    if (!clientId) return;
    fetchEffectiveTerms(clientId)
      .then(t => {
        if (cancelled) return;
        setTerms(t);
        if (t.published && !t.accepted && shownForRef.current !== clientId) {
          shownForRef.current = clientId;
          setAgreed(false); setAcceptError(null); setModalOpen(true);
        }
      })
      .catch(() => { if (!cancelled) setTerms(null); });
    return () => { cancelled = true; };
  }, [clientId]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    resolverRef.current?.();
    resolverRef.current = null;
  }, []);

  const accept = useCallback(async () => {
    if (!clientId) return false;
    setAccepting(true); setAcceptError(null);
    try {
      await acceptTerms(clientId);
      setTerms(await fetchEffectiveTerms(clientId));
      return true;
    } catch {
      setAcceptError('לא ניתן היה לשמור את האישור. נסו שוב.');
      return false;
    } finally {
      setAccepting(false);
    }
  }, [clientId]);

  const remindIfNeeded = useCallback(() => {
    if (!needsAcceptance) return Promise.resolve();
    return new Promise<void>(resolve => {
      resolverRef.current = resolve;
      setAgreed(false); setAcceptError(null); setModalOpen(true);
    });
  }, [needsAcceptance]);

  const handleAccept = async () => {
    if (await accept()) closeModal();
  };

  return (
    <B2BTermsContext.Provider value={{ terms, showTerms: Boolean(terms?.published), needsAcceptance, accept, remindIfNeeded }}>
      {children}
      {modalOpen && terms?.published && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-sky-600" />
                <h3 className="text-lg font-bold text-gray-900">תנאים וכללים</h3>
              </div>
              <button onClick={closeModal} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600">
                {isAgent
                  ? `הלקוח ${clientName} טרם אישר את התנאים העדכניים. ניתן לאשר בשמו — האישור יירשם על שמך.`
                  : 'עודכנו תנאי ההתקשרות. נא לקרוא ולאשר.'}
              </p>
              <TermsSectionsView terms={terms} />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 space-y-3 shrink-0">
              <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer select-none">
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                       className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500" />
                קראתי ואני מסכים/ה
              </label>
              {acceptError && <p className="text-xs text-red-600">{acceptError}</p>}
              <div className="flex items-center justify-end gap-2">
                <button onClick={closeModal}
                        className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  אחר כך
                </button>
                <button onClick={handleAccept} disabled={!agreed || accepting}
                        className="flex items-center gap-2 px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium
                                   rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {accepting && <Loader2 className="w-4 h-4 animate-spin" />}
                  אישור
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </B2BTermsContext.Provider>
  );
}

export function TermsSectionsView({ terms }: { terms: EffectiveTerms }) {
  const sections = TERMS_SECTIONS.filter(s => (terms[s.key] ?? '').trim() !== '');
  if (sections.length === 0) return <p className="text-sm text-gray-400">לא הוגדרו תנאים.</p>;
  return (
    <div className="space-y-4">
      {sections.map(s => (
        <div key={s.key}>
          <h4 className="text-sm font-semibold text-gray-900 mb-1">{s.label}</h4>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{terms[s.key]}</p>
        </div>
      ))}
    </div>
  );
}

export function useB2BTerms(): B2BTermsContextType {
  const ctx = useContext(B2BTermsContext);
  if (!ctx) throw new Error('useB2BTerms must be used inside B2BTermsProvider');
  return ctx;
}
